(() => {
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  let activeDrawer = null;
  let activeOpener = null;
  let drawerHistoryActive = false;
  let ignoreNextPopState = false;
  let touchStartX = 0;
  let touchStartY = 0;
  let touchStartedOnDismissZone = false;

  const drawerHistoryKey = 'anaghayaMobileDrawer';

  const focusableElements = (drawer) =>
    Array.from(drawer.querySelectorAll(focusableSelector)).filter(
      (element) => !element.hidden && element.offsetParent !== null
    );

  const updateBodyState = () => {
    const hasOpenDrawer = Boolean(document.querySelector('.mobile-drawer.is-open'));
    document.body.classList.toggle('nav-overlay-open', hasOpenDrawer);
  };

  const addDrawerHistory = (drawer) => {
    if (window.innerWidth >= 750) return;

    // Give browser/hardware Back gestures a same-page entry to consume.
    const nextState = { ...(window.history.state || {}), [drawerHistoryKey]: drawer.id };
    if (drawerHistoryActive && window.history.state?.[drawerHistoryKey]) {
      window.history.replaceState(nextState, '', window.location.href);
      return;
    }

    window.history.pushState(nextState, '', window.location.href);
    drawerHistoryActive = true;
  };

  const releaseDrawerHistory = (drawer) => {
    if (!drawerHistoryActive || window.history.state?.[drawerHistoryKey] !== drawer?.id) {
      drawerHistoryActive = false;
      return;
    }

    drawerHistoryActive = false;
    ignoreNextPopState = true;
    window.history.back();
  };

  const navigateAfterDrawerClose = (destination) => {
    const drawer = activeDrawer;
    if (!drawerHistoryActive || window.history.state?.[drawerHistoryKey] !== drawer?.id) return false;

    drawerHistoryActive = false;
    closeDrawer(drawer, false, false);
    window.addEventListener('popstate', () => window.location.assign(destination), { once: true });
    window.history.back();
    return true;
  };

  const closeDrawer = (drawer, restoreFocus = true, syncHistory = true) => {
    if (!drawer) return;

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    updateBodyState();

    if (activeDrawer === drawer) {
      const opener = activeOpener;
      opener?.setAttribute('aria-expanded', 'false');
      activeDrawer = null;
      activeOpener = null;
      if (restoreFocus && opener?.isConnected) opener.focus();
    }

    if (syncHistory) releaseDrawerHistory(drawer);
  };

  const openDrawer = (drawer, opener) => {
    if (!drawer) return;

    if (activeDrawer && activeDrawer !== drawer) closeDrawer(activeDrawer, false, false);
    activeDrawer = drawer;
    activeOpener = opener;
    opener?.setAttribute('aria-expanded', 'true');
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    updateBodyState();
    addDrawerHistory(drawer);

    window.requestAnimationFrame(() => {
      const initialFocus = drawer.querySelector('[data-drawer-initial-focus]');
      const focusTarget = initialFocus || focusableElements(drawer)[0] || drawer.querySelector('.mobile-drawer__content');
      window.requestAnimationFrame(() => {
        focusTarget?.focus({ preventScroll: true });
        window.setTimeout(() => {
          if (activeDrawer === drawer && document.activeElement !== focusTarget) {
            focusTarget?.focus({ preventScroll: true });
          }
        }, 120);
      });
    });
  };

  const selectTab = (tab) => {
    const drawer = tab.closest('.mobile-drawer');
    const targetId = tab.getAttribute('aria-controls');
    if (!drawer || !targetId) return;

    drawer.querySelectorAll('[role="tab"]').forEach((candidate) => {
      const selected = candidate === tab;
      candidate.classList.toggle('is-active', selected);
      candidate.setAttribute('aria-selected', selected.toString());
      candidate.tabIndex = selected ? 0 : -1;
    });

    drawer.querySelectorAll('[role="tabpanel"]').forEach((panel) => {
      const selected = panel.id === targetId;
      panel.classList.toggle('is-active', selected);
      panel.hidden = !selected;
    });
  };

  document.addEventListener('click', (event) => {
    const drawerLink = event.target.closest('a[href]');
    if (
      activeDrawer?.contains(drawerLink) &&
      !drawerLink.hasAttribute('download') &&
      !drawerLink.target &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey
    ) {
      const destination = new URL(drawerLink.href, window.location.href);
      if (destination.origin === window.location.origin && navigateAfterDrawerClose(destination.href)) {
        event.preventDefault();
        return;
      }
    }

    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      event.preventDefault();
      openDrawer(document.getElementById(opener.dataset.drawerOpen), opener);
      return;
    }

    const closer = event.target.closest('[data-drawer-close]');
    if (closer) {
      event.preventDefault();
      closeDrawer(closer.closest('.mobile-drawer'));
      return;
    }

    const tab = event.target.closest('[role="tab"]');
    if (tab) selectTab(tab);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!activeDrawer?.contains(form) || form.method.toLowerCase() !== 'get') return;

    const destination = new URL(form.action, window.location.href);
    new FormData(form).forEach((value, key) => destination.searchParams.append(key, value));
    if (navigateAfterDrawerClose(destination.href)) event.preventDefault();
  });

  window.addEventListener('popstate', () => {
    if (ignoreNextPopState) {
      ignoreNextPopState = false;
      return;
    }

    if (!activeDrawer || !drawerHistoryActive) return;
    drawerHistoryActive = false;
    closeDrawer(activeDrawer, false, false);
  });

  document.addEventListener(
    'touchstart',
    (event) => {
      if (!activeDrawer || event.touches.length !== 1) return;
      const touch = event.touches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      touchStartedOnDismissZone = Boolean(
        event.target.closest('.mobile-drawer__header, .mobile-search__heading-row')
      );
    },
    { passive: true, capture: true }
  );

  document.addEventListener(
    'touchmove',
    (event) => {
      if (!activeDrawer || event.touches.length !== 1) return;
      const touch = event.touches[0];
      const deltaX = touch.clientX - touchStartX;
      const deltaY = touch.clientY - touchStartY;

      if (Math.abs(deltaX) > 12 && Math.abs(deltaX) > Math.abs(deltaY) && event.cancelable) {
        event.preventDefault();
      }
    },
    { passive: false, capture: true }
  );

  document.addEventListener(
    'touchend',
    (event) => {
      if (!activeDrawer || !touchStartedOnDismissZone || event.changedTouches.length !== 1) return;
      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - touchStartX;
      const deltaY = touch.clientY - touchStartY;

      touchStartedOnDismissZone = false;
      if (deltaY > 72 && Math.abs(deltaY) > Math.abs(deltaX)) closeDrawer(activeDrawer);
    },
    { passive: true, capture: true }
  );

  document.addEventListener(
    'touchcancel',
    () => {
      touchStartedOnDismissZone = false;
    },
    { passive: true, capture: true }
  );

  document.addEventListener('keydown', (event) => {
    const currentTab = event.target.closest?.('[role="tab"]');
    if (currentTab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      const tabs = Array.from(currentTab.closest('[role="tablist"]').querySelectorAll('[role="tab"]'));
      const currentIndex = tabs.indexOf(currentTab);
      const nextIndex = event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? tabs.length - 1
          : (currentIndex + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
      event.preventDefault();
      selectTab(tabs[nextIndex]);
      tabs[nextIndex].focus();
      return;
    }

    if (!activeDrawer) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer(activeDrawer);
      return;
    }

    if (event.key !== 'Tab') return;
    const focusable = focusableElements(activeDrawer);
    if (!focusable.length) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
})();
